import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Facebook, Instagram, Newspaper, Search } from "lucide-react";
import { SubpageHeader } from "../components/SubpageHeader";
import { Reveal } from "../components/Reveal";
import { useLanguage } from "../i18n/LanguageContext";
import { useSEO, breadcrumbLd, SITE_URL } from "../components/SEO";
import { ARTISTS } from "../data/artistas";
import { SOCIAL } from "../data/social";
import { track } from "../analytics";

const API = process.env.REACT_APP_BACKEND_URL;

const DATE_LOCALE = { pt: "pt-PT", de: "de-CH", fr: "fr-CH", it: "it-CH", en: "en-GB" };
const BRAND_FALLBACK = "/images/noticia-fallback.webp";

const fallbackSrc = () => BRAND_FALLBACK;

const fmtDate = (iso, lang) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat(DATE_LOCALE[lang] || "pt-PT", { day: "numeric", month: "long", year: "numeric" }).format(d);
};

const FeaturedSocial = ({ post, posts, t, lang }) => {
    if (!post && !SOCIAL.instagram) return null;
    // Sem publicação em destaque: cartão "Siga-nos" do perfil oficial.
    if (!post) {
        return (
            <Reveal className="mt-16">
                <section aria-label={t.noticias.socialSeguir} data-testid="social-featured"
                    className="border border-[#c9a24b]/25 bg-[#0a0705]/70 p-8 sm:p-12 text-center">
                    <div className="flex items-center justify-center gap-3">
                        <span className="h-px w-10 bg-[#c9a24b]/50" aria-hidden="true" />
                        <Instagram size={18} strokeWidth={1.5} className="text-[#c9a24b]" />
                        <h2 className="font-caps text-[11px] tracking-[0.35em] uppercase text-[#c9a24b]" data-testid="social-featured-title">
                            {t.noticias.socialSeguir}
                        </h2>
                        <span className="h-px w-10 bg-[#c9a24b]/50" aria-hidden="true" />
                    </div>
                    <p className="font-display font-medium gold-text text-2xl sm:text-3xl mt-6" data-testid="social-featured-handle">
                        @fadosamesa
                    </p>
                    <p className="font-display font-light text-lg text-[#efe6d6]/70 leading-relaxed max-w-xl mx-auto mt-4" data-testid="social-featured-bio">
                        {t.noticias.socialBio}
                    </p>
                    <div className="mt-6 flex flex-wrap justify-center gap-3">
                        <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer"
                            data-testid="social-follow-button"
                            className="inline-flex items-center gap-2 font-caps text-[11px] tracking-[0.25em] uppercase text-[#c9a24b] border border-[#c9a24b]/60 px-8 py-3.5 hover:bg-[#c9a24b] hover:text-[#070503] transition-colors duration-400">
                            <Instagram size={14} strokeWidth={1.6} /> Instagram
                        </a>
                        <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer"
                            data-testid="social-follow-facebook"
                            className="inline-flex items-center gap-2 font-caps text-[11px] tracking-[0.25em] uppercase text-[#c9a24b] border border-[#c9a24b]/60 px-8 py-3.5 hover:bg-[#c9a24b] hover:text-[#070503] transition-colors duration-400">
                            <Facebook size={14} strokeWidth={1.6} /> Facebook
                        </a>
                    </div>
                </section>
            </Reveal>
        );
    }
    const Icon = post.platform === "instagram" ? Instagram : Facebook;
    if (posts && posts.length > 1) {
        return (
            <Reveal className="mt-16">
                <section aria-label={t.noticias.socialTituloMulti} data-testid="social-featured"
                    className="border border-[#c9a24b]/25 bg-[#0a0705]/70 p-6 sm:p-10">
                    <div className="flex items-center gap-3">
                        <Icon size={18} strokeWidth={1.5} className="text-[#c9a24b]" />
                        <h2 className="font-caps text-[11px] tracking-[0.35em] uppercase text-[#c9a24b]" data-testid="social-featured-title">
                            {t.noticias.socialTituloMulti}
                        </h2>
                    </div>
                    <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="social-featured-grid">
                        {posts.map((p) => (
                            <div key={p.id} className="flex flex-col border border-[#c9a24b]/20 bg-[#070503]/60 p-5" data-testid={`social-post-${p.id}`}>
                                <p className="font-caps text-[9px] tracking-[0.25em] uppercase text-[#efe6d6]/50" data-testid={`social-post-date-${p.id}`}>
                                    Instagram · {fmtDate(p.published_at, lang)}
                                </p>
                                <p className="font-display font-light text-base text-[#efe6d6]/85 leading-relaxed mt-3 flex-1 line-clamp-4" data-testid={`social-post-caption-${p.id}`}>
                                    {p.caption}
                                </p>
                                <a href={p.post_url} target="_blank" rel="noopener noreferrer"
                                    data-testid={`social-post-link-${p.id}`}
                                    className="mt-4 inline-block text-center font-caps text-[10px] tracking-[0.25em] uppercase text-[#c9a24b] border border-[#c9a24b]/50 px-4 py-2.5 hover:bg-[#c9a24b] hover:text-[#070503] transition-colors duration-400">
                                    {t.noticias.socialVer}
                                </a>
                            </div>
                        ))}
                    </div>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer"
                            data-testid="social-follow-button"
                            className="inline-flex items-center gap-2 font-caps text-[11px] tracking-[0.25em] uppercase text-[#c9a24b] border border-[#c9a24b]/60 px-8 py-3.5 hover:bg-[#c9a24b] hover:text-[#070503] transition-colors duration-400">
                            <Instagram size={14} strokeWidth={1.6} /> {t.noticias.socialSeguir} @fadosamesa
                        </a>
                        <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer"
                            data-testid="social-follow-facebook"
                            className="inline-flex items-center gap-2 font-caps text-[11px] tracking-[0.25em] uppercase text-[#c9a24b] border border-[#c9a24b]/60 px-8 py-3.5 hover:bg-[#c9a24b] hover:text-[#070503] transition-colors duration-400">
                            <Facebook size={14} strokeWidth={1.6} /> Facebook
                        </a>
                    </div>
                </section>
            </Reveal>
        );
    }
    return (
        <Reveal className="mt-16">
            <section aria-label={t.noticias.socialTitulo} data-testid="social-featured"
                className="border border-[#c9a24b]/25 bg-[#0a0705]/70 p-6 sm:p-10">
                <div className="flex items-center gap-3">
                    <Icon size={18} strokeWidth={1.5} className="text-[#c9a24b]" />
                    <h2 className="font-caps text-[11px] tracking-[0.35em] uppercase text-[#c9a24b]" data-testid="social-featured-title">
                        {t.noticias.socialTitulo}
                    </h2>
                </div>
                <div className={`mt-8 grid gap-8 ${post.image ? "md:grid-cols-[320px_1fr]" : ""} items-start`}>
                    {post.image && (
                        <img loading="lazy" decoding="async" src={post.image} alt={post.caption}
                            onError={(e) => { if (!e.currentTarget.dataset.fb) { e.currentTarget.dataset.fb = "1"; e.currentTarget.src = "/images/ambiente.webp"; } }}
                            className="w-full aspect-square object-cover border border-[#c9a24b]/15"
                            data-testid="social-featured-image" />
                    )}
                    <div className="flex flex-col gap-5">
                        {post.published_at && (
                            <p className="font-caps text-[10px] tracking-[0.25em] uppercase text-[#efe6d6]/50" data-testid="social-featured-date">
                                {fmtDate(post.published_at, lang)}
                            </p>
                        )}
                        <p className="font-display font-light text-lg sm:text-xl text-[#efe6d6]/85 leading-relaxed" data-testid="social-featured-caption">
                            {post.caption}
                        </p>
                        <div className="flex flex-wrap gap-4 mt-2">
                            <a href={post.post_url} target="_blank" rel="noopener noreferrer"
                                data-testid="social-view-button"
                                className="font-caps text-[11px] tracking-[0.25em] uppercase text-[#070503] bg-[#c9a24b] px-6 py-3 hover:bg-[#ecd49b] transition-colors duration-400">
                                {t.noticias.socialVer}
                            </a>
                            <a href={post.profile_url} target="_blank" rel="noopener noreferrer"
                                data-testid="social-follow-button"
                                className="font-caps text-[11px] tracking-[0.25em] uppercase text-[#c9a24b] border border-[#c9a24b]/60 px-6 py-3 hover:bg-[#c9a24b] hover:text-[#070503] transition-colors duration-400">
                                {t.noticias.socialSeguir}
                            </a>
                            <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer"
                                data-testid="social-follow-facebook"
                                className="inline-flex items-center gap-2 font-caps text-[11px] tracking-[0.25em] uppercase text-[#c9a24b] border border-[#c9a24b]/60 px-6 py-3 hover:bg-[#c9a24b] hover:text-[#070503] transition-colors duration-400">
                                <Facebook size={14} strokeWidth={1.6} /> Facebook
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </Reveal>
    );
};

const NewsCard = ({ article, t, lang, index }) => {
    return (
        <Reveal delay={(index % 3) * 0.08} className="flex flex-col h-full">
            <article data-testid={`news-card-${article.id}`}
                className="group flex-1 flex flex-col border border-[#c9a24b]/15 bg-[#0a0705]/60 overflow-hidden hover:border-[#c9a24b]/45 transition-colors duration-500">
                <div className="aspect-[16/9] overflow-hidden bg-[#070503]">
                    <img loading="lazy" decoding="async"
                        src={article.image || fallbackSrc(article)}
                        alt={article.title}
                        onError={(e) => { if (!e.currentTarget.dataset.fb) { e.currentTarget.dataset.fb = "1"; e.currentTarget.src = fallbackSrc(article); } }}
                        className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.03] transition-[opacity,transform] duration-700"
                        data-testid={`news-image-${article.id}`} />
                </div>
                <div className="p-6 flex flex-col gap-3 flex-1">
                    <div className="flex items-center justify-between gap-3 font-caps text-[10px] tracking-[0.22em] uppercase text-[#c9a24b]/80">
                        <span data-testid={`news-date-${article.id}`}>{fmtDate(article.published_at, lang)}</span>
                        {article.source && (
                            <span className="text-[#efe6d6]/45 truncate max-w-[70%]" data-testid={`news-source-${article.id}`}>
                                {t.noticias.fonte}: {article.source}
                            </span>
                        )}
                    </div>
                    <h3 className="font-display font-medium text-lg sm:text-xl leading-snug text-[#efe6d6]" data-testid={`news-headline-${article.id}`}>
                        {article.title}
                    </h3>
                    {article.excerpt && (
                        <p className="text-sm font-light text-[#efe6d6]/60 leading-relaxed line-clamp-3" data-testid={`news-excerpt-${article.id}`}>
                            {article.excerpt}
                        </p>
                    )}
                    <a href={article.url} {...(article.url.startsWith("/") ? {} : { target: "_blank", rel: "noopener noreferrer" })}
                        data-testid={`news-readmore-${article.id}`}
                        className="mt-auto pt-3 inline-flex items-center gap-2 font-caps text-[11px] tracking-[0.25em] uppercase text-[#c9a24b] hover:text-[#ecd49b] transition-colors duration-300">
                        {t.noticias.lerMais}
                        <ArrowUpRight size={14} strokeWidth={1.5} />
                    </a>
                </div>
            </article>
        </Reveal>
    );
};

export default function NoticiasPage() {
    const { t, lang } = useLanguage();
    const [artist, setArtist] = useState("todas");
    const [input, setInput] = useState("");
    const [search, setSearch] = useState("");
    const [articles, setArticles] = useState([]);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [social, setSocial] = useState(null);

    const FILTERS = useMemo(() => ([
        { id: "todas", label: t.noticias.filtros.todas },
        { id: "fados-a-mesa", label: t.noticias.filtros.projeto },
        ...ARTISTS.map((a) => ({ id: a.slug, label: a.name })),
    ]), [t]);

    const jsonLd = useMemo(() => ([
        {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: t.seo.noticias.title,
            description: t.seo.noticias.desc,
            url: `${SITE_URL}/noticias`,
            inLanguage: lang,
        },
        breadcrumbLd([{ name: t.bc.home, path: "/" }, { name: t.bc.noticias, path: "/noticias" }]),
        ...(articles.length ? [{
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement: articles.map((a, i) => {
                const artist = a.artist ? ARTISTS.find((x) => x.slug === a.artist) : null;
                return {
                    "@type": "ListItem",
                    position: i + 1,
                    item: {
                        "@type": "NewsArticle",
                        headline: a.title,
                        url: a.url,
                        ...(a.image ? { image: a.image } : {}),
                        ...(a.published_at ? { datePublished: a.published_at } : {}),
                        ...(a.source ? { author: { "@type": "Organization", name: a.source } } : {}),
                        ...(artist ? { about: { "@type": "Person", name: artist.name, url: `${SITE_URL}/artistas/${artist.slug}` } } : {}),
                    },
                };
            }),
        }] : []),
    ]), [t, lang, articles]);

    useSEO({ title: t.seo.noticias.title, description: t.seo.noticias.desc, jsonLd });

    const load = useCallback(async (pageToLoad, append) => {
        if (append) setLoadingMore(true); else setLoading(true);
        try {
            const params = new URLSearchParams({ artist, page: String(pageToLoad), limit: "12", lang });
            if (search) params.set("q", search);
            const r = await fetch(`${API}/api/news?${params}`);
            if (!r.ok) throw new Error("news fetch failed");
            const data = await r.json();
            setArticles((prev) => (append ? [...prev, ...data.articles] : data.articles));
            setPage(data.page);
            setPages(data.pages);
        } catch {
            if (!append) { setArticles([]); setPages(1); }
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [artist, search, lang]);

    useEffect(() => { load(1, false); }, [load]);

    useEffect(() => {
        fetch(`${API}/api/social/latest`)
            .then((r) => r.json())
            .then((d) => setSocial(d))
            .catch(() => {});
    }, []);

    const submitSearch = (e) => {
        e.preventDefault();
        setSearch(input.trim());
        if (input.trim()) track("Search", { search_string: input.trim() });
    };

    return (
        <div className="min-h-screen bg-[#070503] text-[#efe6d6] relative overflow-hidden" data-testid="noticias-page">
            <div className="absolute inset-0 pointer-events-none">
                <img loading="lazy" decoding="async" src="/images/ambiente.webp" alt="" aria-hidden="true" className="w-full h-full object-cover opacity-25 blur-md scale-110" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#070503]/60 via-[#070503]/80 to-[#070503]" />
            </div>
            <SubpageHeader />
            <motion.main
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-10 mx-auto max-w-[1200px] px-5 sm:px-10 pt-48 sm:pt-36 pb-24"
            >
                <Reveal className="text-center">
                    <p className="font-caps text-[11px] tracking-[0.35em] uppercase text-[#c9a24b]">{t.noticias.label}</p>
                    <div className="flex justify-center mt-4">
                        <img loading="lazy" decoding="async" src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-16 opacity-90" />
                    </div>
                    <h1 className="font-display font-medium gold-text text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-6" data-testid="noticias-title">
                        {t.noticias.titulo}
                    </h1>
                    <p className="font-display font-light text-lg sm:text-xl text-[#efe6d6]/70 leading-relaxed max-w-3xl mx-auto mt-8" data-testid="noticias-intro">
                        {t.noticias.intro}
                    </p>
                </Reveal>

                {/* Faixa CTA única e estratégica: topo da página, logo após o intro */}
                <Reveal className="mt-12">
                    <div className="border border-[#c9a24b]/25 bg-[#0a0705]/70 px-6 py-6 sm:px-10 flex flex-col sm:flex-row items-center justify-between gap-6" data-testid="news-cta-banda">
                        <div className="text-center sm:text-left">
                            <p className="font-caps text-[10px] tracking-[0.3em] uppercase gold-text" data-testid="news-cta-banda-titulo">{t.noticias.ctaTitulo}</p>
                            <p className="mt-2 font-display font-light text-base sm:text-lg text-[#efe6d6]/70" data-testid="news-cta-banda-info">
                                {t.hero.data} · {t.hero.local}, Emmenbrücke
                            </p>
                        </div>
                        <Link
                            to="/bilhetes"
                            data-testid="news-cta-banda-button"
                            className="shrink-0 font-caps text-[11px] tracking-[0.25em] uppercase text-[#070503] bg-[#c9a24b] px-8 py-3.5 hover:bg-[#ecd49b] transition-colors duration-400"
                        >
                            {t.noticias.ctaReservar}
                        </Link>
                    </div>
                </Reveal>

                <FeaturedSocial post={social?.post || null} posts={social?.posts || null} t={t} lang={lang} />

                <div className="mt-16 flex flex-col gap-8" data-testid="news-controls">
                    <div className="flex flex-wrap justify-center gap-2.5" role="group" aria-label={t.noticias.ultimasTitulo}>
                        {FILTERS.map((f) => (
                            <button
                                key={f.id}
                                onClick={() => setArtist(f.id)}
                                aria-pressed={artist === f.id}
                                data-testid={`news-filter-${f.id}`}
                                className={`font-caps text-[10px] sm:text-[11px] tracking-[0.2em] uppercase px-4 py-2 border transition-colors duration-300 ${
                                    artist === f.id
                                        ? "text-[#070503] bg-[#c9a24b] border-[#c9a24b]"
                                        : "text-[#efe6d6]/70 border-[#c9a24b]/25 hover:border-[#c9a24b]/60 hover:text-[#c9a24b]"
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                    <form onSubmit={submitSearch} className="flex max-w-xl mx-auto w-full" role="search">
                        <input
                            type="search"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={t.noticias.pesquisaPh}
                            aria-label={t.noticias.pesquisaPh}
                            data-testid="news-search-input"
                            className="flex-1 bg-[#0a0705]/80 border border-[#c9a24b]/25 border-r-0 px-5 py-3 text-sm font-light text-[#efe6d6] placeholder:text-[#efe6d6]/35 focus:outline-none focus:border-[#c9a24b]/60 transition-colors duration-300"
                        />
                        <button type="submit" data-testid="news-search-submit" aria-label={t.noticias.pesquisaPh}
                            className="bg-[#c9a24b] text-[#070503] px-5 hover:bg-[#ecd49b] transition-colors duration-300">
                            <Search size={17} strokeWidth={1.8} />
                        </button>
                    </form>
                </div>

                <div className="mt-16">
                    <Reveal className="text-center">
                        <h2 className="font-caps text-xs tracking-[0.35em] uppercase text-[#c9a24b]" data-testid="news-latest-title">
                            {t.noticias.ultimasTitulo}
                        </h2>
                    </Reveal>

                    {loading ? (
                        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="news-loading">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className="border border-[#c9a24b]/10 bg-[#0a0705]/40 animate-pulse">
                                    <div className="aspect-[16/9] bg-[#c9a24b]/5" />
                                    <div className="p-6 space-y-3">
                                        <div className="h-2.5 bg-[#c9a24b]/10 w-1/3" />
                                        <div className="h-4 bg-[#c9a24b]/10 w-full" />
                                        <div className="h-4 bg-[#c9a24b]/10 w-2/3" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : articles.length === 0 ? (
                        <div className="mt-10 border border-dashed border-[#c9a24b]/30 bg-[#0a0705]/60 py-16 px-6 flex flex-col items-center gap-4 text-center" data-testid="news-empty">
                            <Newspaper size={30} strokeWidth={1} className="text-[#c9a24b]/50" />
                            <p className="font-display font-light text-lg text-[#efe6d6]/60 max-w-md">{t.noticias.semResultados}</p>
                        </div>
                    ) : (
                        <>
                            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="news-grid">
                                {articles.map((a, i) => (
                                    <NewsCard key={a.id} article={a} t={t} lang={lang} index={i} />
                                ))}
                            </div>
                            {page < pages && (
                                <div className="mt-12 text-center">
                                    <button
                                        onClick={() => load(page + 1, true)}
                                        disabled={loadingMore}
                                        data-testid="news-load-more"
                                        className="font-caps text-xs tracking-[0.25em] uppercase text-[#c9a24b] border border-[#c9a24b]/60 px-10 py-4 hover:bg-[#c9a24b] hover:text-[#070503] disabled:opacity-50 transition-colors duration-400"
                                    >
                                        {loadingMore ? t.noticias.aCarregar : t.noticias.carregarMais}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </motion.main>
        </div>
    );
}
